import {
  Button,
  ButtonGroup,
  Stack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import {
  BlockerFunction,
  Link as RLink,
  useBlocker,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import LoadingSpinner from "components/LoadingSpinner";
import PreventNavigationAlert from "components/PreventNavigationAlert";
import {
  RequestErrorModals,
  RequestProcessingModal,
} from "components/RequestModals";
import SubmitButton from "components/SubmitButton";
import { contractsPath } from "constants/paths";
import { useCustomerContext } from "contexts/CustomerProvider";
import useCreateRequestContract from "hooks/api/requestContract/useCreateRequestContract";
import useCheckRequiredFieldsFilled from "hooks/useCheckRequiredFieldsFilled";
import useGetProductList from "hooks/api/products/useGetProductList";
import usePreventNavigation from "hooks/usePreventNavigation";
import { RequestContractValues } from "services/ContractService";
import { formatDateForApi } from "services/apiHelpers";
import { getDateAtMidnight } from "util/constants";
import BasicContractInfoInputs from "./BasicContractInfoInputs";
import ContractLineItems from "./ContractLineItems";
import { RequestContractConfirmationModal } from "./modals";
import { requestContractSchema } from "./requestContractSchema";

export type RequestContractFormLineItem = {
  shipFrom: SelectOption | null;
  shipTo: SelectOption | null;
  salesTerms: SelectOption | null;
  mode: SelectOption | null;
};

export type RequestContractFormProps = {
  product: SelectOption | null;
  startShipDate: Date | undefined;
  endShipDate: Date | undefined;
  quantity: number | "";
  poNumber: string | undefined;
  lineItems: RequestContractFormLineItem[];
};

export const emptyLineItem: RequestContractFormLineItem = {
  shipFrom: null,
  shipTo: null,
  salesTerms: null,
  mode: null,
};

// Default startShipDate is dynamic
const defaultValues: Omit<RequestContractFormProps, "startShipDate"> = {
  product: null,
  endShipDate: undefined,
  quantity: 1,
  poNumber: "",
  lineItems: [emptyLineItem],
};

export const filterOutEmptyLineItems = (
  lineItem: RequestContractFormLineItem
) => !Object.values(lineItem).every((value) => value === null);

const RequestContractForm = () => {
  const { selectedCustomers } = useCustomerContext();
  const { data: productOptions, isLoading } = useGetProductList();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search] = useSearchParams();
  const {
    mutateAsync,
    isLoading: isCreateRequestContractLoading,
    isError,
    error,
    reset: resetCreateContractRequest,
  } = useCreateRequestContract();
  const methods = useForm<RequestContractFormProps>({
    defaultValues: { ...defaultValues, startShipDate: getDateAtMidnight() },
    resolver: yupResolver<
      Omit<RequestContractFormProps, "quantity" | "lineItems"> & {
        quantity: any;
        lineItems: any;
      }
    >(requestContractSchema),
  });

  const [failureCount, setFailureCount] = useState(0);

  const onSubmit: SubmitHandler<RequestContractFormProps> = useCallback(() => {
    setFailureCount(0);
    onOpen();
    resetCreateContractRequest();
  }, [onOpen, resetCreateContractRequest]);

  const {
    reset,
    formState: { isSubmitting, isSubmitted, isDirty },
    watch,
    handleSubmit,
  } = methods;

  const [product, startShipDate, endShipDate, quantity, lineItems] = watch([
    "product",
    "startShipDate",
    "endShipDate",
    "quantity",
    "lineItems",
  ]);

  const message =
    "If you navigate away from this page, your changes will be lost. Are you sure you want to continue?";

  // prevents navigation away from the app when form is dirty
  usePreventNavigation({
    enabled: isDirty,
    message,
  });

  // prevents in app navigation when form is dirty
  const shouldBlock = useCallback<BlockerFunction>(() => {
    if (isSubmitted) {
      return false;
    }
    // product is reset in a useEffect below preventing isDirty from flipping to true
    // so explicitly checking it here to prevent navigation if this field has a value
    return !!product || isDirty;
  }, [isDirty, isSubmitted, product]);
  const blocker = useBlocker(shouldBlock);

  const uom = useMemo(
    () =>
      productOptions
        ?.map((productOptionGroup) => productOptionGroup.options)
        .flat()
        .find((productOption) => productOption.value === product?.value)?.uom ??
      undefined,
    [productOptions, product]
  );

  const basicInfoFilled = useCheckRequiredFieldsFilled([
    product,
    startShipDate,
    endShipDate,
    quantity,
  ]);

  // Check for any partially filled in line items. All empty or all filled are valid.
  const lineItemsAllFilled = !lineItems.some((lineItem) => {
    const selectValues = Object.values(lineItem);
    const filledInSelects = selectValues.filter((value) => value === null);
    return (
      filledInSelects.length !== 0 &&
      filledInSelects.length !== selectValues.length
    );
  });

  const oneLineItemAllFilled = lineItems.some((lineItem) =>
    Object.values(lineItem).every(Boolean)
  );

  const formFieldsFilled =
    basicInfoFilled && lineItemsAllFilled && oneLineItemAllFilled;

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "product") {
        reset({
          ...defaultValues,
          product: value.product, // Keep the newly selected product
          startShipDate: getDateAtMidnight(), // Should be dynamic
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [reset, watch]);

  const toast = useToast();
  const navigate = useNavigate();

  const onConfirm: SubmitHandler<RequestContractFormProps> = useCallback(
    (data) => {
      onClose();
      const values: RequestContractValues = {
        product: data.product?.value ?? "",
        quantity: Number(data.quantity),
        shipDateStart: data.startShipDate
          ? formatDateForApi(data.startShipDate)
          : "",
        shipDateEnd: data.endShipDate ? formatDateForApi(data.endShipDate) : "",
        poNumber: data.poNumber,
        uomCode: uom ?? "ST",
        contractLines: data.lineItems
          .filter(filterOutEmptyLineItems)
          .map((lineItem) => ({
            shipFromCode: lineItem.shipFrom?.value ?? "",
            shipToCode: lineItem.shipTo?.value ?? "",
            modeOfTransportationCode: lineItem.mode?.value ?? "",
            salesTermsCode: lineItem.salesTerms?.value ?? "",
          })),
      };
      return mutateAsync(values)
        .then(() => {
          setFailureCount(0);
          toast({
            position: "top-right",
            description: "Successfully requested contract",
            duration: 2000,
            status: "success",
          });
          navigate(`/${contractsPath}?${search}`);
        })
        .catch(() => {
          setFailureCount((_failureCount) => _failureCount + 1);
        });
    },
    [mutateAsync, navigate, onClose, search, toast, uom]
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={6}>
            <BasicContractInfoInputs uom={uom} />
            <ContractLineItems />
            <ButtonGroup alignSelf="flex-end">
              <Button
                as={RLink}
                to={`/${contractsPath}?${search}`}
                variant="secondary"
                colorScheme="grey"
              >
                Cancel
              </Button>
              <SubmitButton isDisabled={isSubmitting || !formFieldsFilled}>
                Submit
              </SubmitButton>
            </ButtonGroup>
          </Stack>
        </form>
        <RequestContractConfirmationModal
          isOpen={isOpen}
          onClose={onClose}
          onConfirm={handleSubmit(onConfirm)}
          uom={uom}
        />
        <RequestProcessingModal isOpen={isCreateRequestContractLoading}>
          Your contract request is being processed. This may take up to 1
          minute, your page will refresh when complete.
        </RequestProcessingModal>
        {isError && (
          <RequestErrorModals
            isOpen={isError}
            onClose={resetCreateContractRequest}
            onRetry={handleSubmit(onConfirm)}
            failureCount={failureCount}
            error={error}
            customerName={selectedCustomers[0].name}
          />
        )}
      </FormProvider>
      <PreventNavigationAlert
        onClose={() => blocker.reset?.()}
        isOpen={blocker.state === "blocked"}
        onContinue={() => blocker.proceed?.()}
      />
    </>
  );
};

export default RequestContractForm;
